import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Article } from '@troc/shared'
import {
  anciennete,
  codeFamille,
  emojiArticle,
  eur,
  MOIS_FIN_CONTRAT,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { NiveauxDemarque } from '../composants/NiveauxDemarque'
import { Ecran } from '../composants/Ecran'
import { PastilleStatut } from '../composants/Pastille'
import { imprimerEtiquettes } from '../impression/documents'
import { useToast } from '../etat/Toast'
import { useReveil } from '../composants/Volets'

const FILTRES_STATUT = [
  { cle: 'tous', libelle: 'Tous' },
  { cle: 'en_rayon', libelle: 'En rayon' },
  { cle: 'a_recuperer', libelle: 'À récupérer' },
  { cle: 'vendu', libelle: 'Vendus' },
]

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit' })

/** Une famille telle qu'elle se présente dans la grille d'entrée. */
interface FamilleStock {
  code: string
  libelle: string
  emoji: string
  lignes: number
  /** Exemplaires, et non lignes : c'est la place occupée en rayon. */
  pieces: number
  valeurCentimes: number
  plusAncien: string | null
}

export function Articles() {
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [requete, setRequete] = useState('')
  const [categorie, setCategorie] = useState('Tous')
  const [statut, setStatut] = useState('tous')
  const [articles, setArticles] = useState<Article[]>([])
  const [familles, setFamilles] = useState<FamilleStock[]>([])
  const [chargement, setChargement] = useState(true)
  const [rafraichir, setRafraichir] = useState(0)
  const [total, setTotal] = useState(0)
  const [suiteEnCours, setSuiteEnCours] = useState(false)
  /** Sentinelle de bas de liste : la voir, c'est charger la suite. */
  const sentinelle = useRef<HTMLDivElement>(null)

  /** Remplace un article en place après un réglage, sans recharger la liste. */
  const remplacer = (article: Article) =>
    setArticles((liste) => liste.map((a) => (a.id === article.id ? article : a)))

  useEffect(() => {
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      setChargement(true)
      const parametres = new URLSearchParams({ statut, limite: '400' })
      if (requete) parametres.set('q', requete)
      if (categorie !== 'Tous') parametres.set('categorie', categorie)
      api<{ articles: Article[]; total: number }>(`/articles?${parametres}`, {
        signal: controleur.signal,
      })
        .then((r) => {
          setArticles(r.articles)
          setTotal(r.total)
        })
        .catch(() => undefined)
        .finally(() => setChargement(false))
    }, 220)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [requete, categorie, statut, rafraichir])

  /**
   * La tranche suivante du rayon. Le magasin en compte 80 034 pièces : elles
   * sont toutes accessibles, mais envoyées à mesure qu'on descend — un tableau
   * de quatre-vingt mille lignes d'un coup fige Safari.
   */
  const chargerSuite = useCallback(() => {
    if (suiteEnCours || articles.length === 0 || articles.length >= total) return
    setSuiteEnCours(true)
    const parametres = new URLSearchParams({
      statut,
      limite: '400',
      depuis: String(articles.length),
      ...(requete ? { q: requete } : {}),
      ...(categorie !== 'Tous' ? { categorie } : {}),
    })
    api<{ articles: Article[] }>(`/articles?${parametres}`)
      .then((r) => setArticles((actuels) => [...actuels, ...r.articles]))
      .catch(() => undefined)
      .finally(() => setSuiteEnCours(false))
  }, [articles.length, total, statut, requete, categorie, suiteEnCours])

  useEffect(() => {
    const cible = sentinelle.current
    if (!cible) return
    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((e) => e.isIntersecting)) chargerSuite()
      },
      { rootMargin: '600px' },
    )
    observateur.observe(cible)
    return () => observateur.disconnect()
  }, [chargerSuite])

  // La grille des familles ne dépend que du filtre de statut : elle reste
  // stable pendant qu'on tape une recherche, et sert de repère fixe.
  useEffect(() => {
    api<{ familles: FamilleStock[] }>(`/familles?statut=${statut}`)
      .then((r) => setFamilles(r.familles))
      .catch(() => undefined)
  }, [statut, articles.length])

  const totalPieces = familles.reduce((somme, f) => somme + f.pieces, 0)
  const totalValeur = familles.reduce((somme, f) => somme + f.valeurCentimes, 0)

  // L'écran reste monté quand on le quitte : au retour, le stock a pu changer.
  useReveil('/articles', () => setRafraichir((n) => n + 1))

  return (
    <Ecran
      titre="Articles"
      sousTitre={`${articles.length} article${articles.length > 1 ? 's' : ''}`}
      outils={
        <div className="seg" style={{ marginLeft: 'auto' }}>
          {FILTRES_STATUT.map((filtre) => (
            <button
              key={filtre.cle}
              type="button"
              className="seg-opt"
              aria-pressed={statut === filtre.cle}
              onClick={() => setStatut(filtre.cle)}
            >
              {filtre.libelle}
            </button>
          ))}
        </div>
      }
    >
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '2px solid var(--color-divider)',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          className="saisie saisie-grande"
          style={{ maxWidth: 420 }}
          placeholder="Rechercher — désignation, n°, déposant"
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-secondaire"
          style={{ marginLeft: 'auto' }}
          disabled={articles.length === 0}
          onClick={() => {
            imprimerEtiquettes(articles.slice(0, 60))
            signaler(
              `${Math.min(articles.length, 60)} étiquettes envoyées à l’imprimante.`,
            )
          }}
        >
          Réimprimer les étiquettes
        </button>
      </div>

      <div className="zone-defilante" style={{ paddingTop: 0 }}>
        {/* Les familles d'abord : on cherche « où sont les luminaires ? » avant
            de chercher un article précis. Un appui filtre la liste dessous. */}
        <div className="grille-familles">
          {/* « Tous » ouvre la grille : c'est l'état de départ, et le chemin de
              retour. Le placer dans la grille plutôt qu'en bouton à part évite
              de chercher ailleurs ce qu'on vient de quitter d'un doigt. */}
          <button
            type="button"
            className="boite-famille boite-tous"
            aria-pressed={categorie === 'Tous'}
            onClick={() => setCategorie('Tous')}
            title={`Toutes les familles — ${eur(totalValeur)}`}
          >
            <span className="boite-emoji" aria-hidden="true">
              ▦
            </span>
            <span className="boite-nom">Toutes les familles</span>
            <span className="boite-compte">{totalPieces}</span>
          </button>
          {familles.map((f) => (
            <button
              key={f.code}
              type="button"
              className="boite-famille"
              aria-pressed={categorie === f.libelle}
              disabled={f.pieces === 0 && categorie !== f.libelle}
              onClick={() => setCategorie(categorie === f.libelle ? 'Tous' : f.libelle)}
              title={`${f.libelle} (${f.code}) — ${f.pieces} pièce${f.pieces > 1 ? 's' : ''} · ${eur(f.valeurCentimes)}`}
            >
              <span className="boite-emoji" aria-hidden="true">
                {f.emoji}
              </span>
              <span className="boite-nom">{f.libelle}</span>
              <span className="boite-compte">{f.pieces}</span>
            </button>
          ))}
        </div>

        <div className="etiquette" style={{ padding: '4px 24px 8px' }}>
          {categorie === 'Tous' ? 'Tous les articles' : categorie} · du plus récent au plus ancien
        </div>

        <table className="tableau">
          <thead>
            <tr>
              <th>N°</th>
              <th>Désignation</th>
              <th>Famille</th>
              <th>Déposant</th>
              <th>Déposé · depuis</th>
              <th className="a-droite">Stock</th>
              <th className="a-droite">Prix d’origine</th>
              <th>Prix de vente</th>
              <th>Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {articles.map((article) => (
              <tr key={article.id} className={`etat-${article.statut}`}>
                <td className="ref">
                  <Link to={`/articles/${article.id}`} style={{ color: 'inherit' }}>
                    {article.ref}
                  </Link>
                </td>
                <td className="titre">{article.titre}</td>
                <td className="sourdine" title={article.categorie}>
                  {codeFamille(article.categorie)}
                </td>
                <td>
                  <Link to={`/deposants/${article.deposantId}`} style={{ color: 'inherit' }}>
                    {article.deposantNom}
                  </Link>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {FORMAT_DATE.format(new Date(article.deposeLe))}
                  <span className="sourdine" style={{ fontSize: 12 }}>
                    {' '}
                    · {anciennete(article.deposeLe)}
                  </span>
                </td>
                <td className="a-droite chiffre">
                  {article.quantite > 1
                    ? `${article.quantiteRestante} / ${article.quantite}`
                    : article.quantiteRestante}
                </td>
                <td className="a-droite chiffre sourdine">
                  {eur(article.prixInitialCentimes)}
                </td>
                <td>
                  {article.statut === 'vendu' || article.statut === 'retire' ? (
                    <span className="chiffre sourdine">{eur(article.prixActuelCentimes)}</span>
                  ) : (
                    <NiveauxDemarque
                      article={article}
                      onArticle={remplacer}
                      onErreur={alerter}
                      variante="liste"
                    />
                  )}
                </td>
                <td>
                  <PastilleStatut statut={article.statut} />
                </td>
                <td className="a-droite">
                  {article.statut !== 'vendu' && article.statut !== 'retire' && (
                    <button
                      type="button"
                      className="btn btn-fantome"
                      onClick={() => naviguer('/caisse', { state: { ajouterRef: article.ref } })}
                    >
                      Encaisser
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {articles.length === 0 && (
          <div className="vide">{chargement ? 'Chargement…' : 'Aucun article ne correspond.'}</div>
        )}
        <div ref={sentinelle} />
        {articles.length > 0 && articles.length < total && (
          <div className="vide sourdine">
            {suiteEnCours
              ? 'Chargement des suivants…'
              : `${articles.length} sur ${total} — continuez à descendre`}
          </div>
        )}
      </div>
    </Ecran>
  )
}
